import React, {useContext, useEffect, useMemo, useState} from 'react';
import {ICommentModel} from '@src/@interfaces/models/Comment/ICommentModel';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import app from '@src/helpers/app';
import {UserContext} from '@src/contexts/UserContext';
import {now, parseDate} from '@src/helpers/dates';
import config from '@src/helpers/config';
import trans from '@src/helpers/trans';
import {LocaleContext} from '@src/contexts/LocaleContext';
import Translate from '@src/components/Translate/Translate';
import RenderIf from '@src/components/RenderIf/RenderIf';

const PERMISSIONS = {
    'can-delete-own-comment-first-time': 'comments@can-delete-own-comments-first-time-after-creation',
    'can-delete-own-comment-anytime': 'comments@can-delete-own-comments-anytime',
    'can-delete-any-comment-anytime': 'comments@can-delete-any-comments-anytime',
};

export default function CommentListItemControls(props: {
    model: ICommentModel,
    allowToReply?: boolean,
    allowToDelete?: boolean,
    onDeleted?: (comment: ICommentModel) => void,
    onReply?: (comment: ICommentModel) => void,
}) {
    const userContext = useContext(UserContext);
    const {locale} = useContext(LocaleContext);
    const [isDisabled, setIsDisabled] = useState(false);
    const isOwnComment = (
            userContext != null &&
            userContext.model?.id != null &&
            userContext.model?.id === props.model.creator_id
    );
    const createdAt = props.model.created_at ? parseDate(props.model.created_at).unix() : null;// seconds
    const firstTimeInMinutes = config('client.comments.first-minutes-to-delete-own-comment-after-creation', 5);
    const isFirstTimeAfterCreation = (
            createdAt &&
            createdAt >= now().add(firstTimeInMinutes, 'minutes').unix()
    );
    const {model: comment, onDeleted, onReply: onReplyBase} = props;
    const {canDelete, onDelete, onReply} = useMemo(() => {
        const canDelete = ((): boolean => {
            if (!userContext || props.allowToDelete === false) {
                return false;
            }

            if (isOwnComment && isFirstTimeAfterCreation) {
                return userContext.can(PERMISSIONS['can-delete-own-comment-first-time']);
            }

            if (isOwnComment) {
                return userContext.can(PERMISSIONS['can-delete-own-comment-anytime']);
            }

            return userContext.can(PERMISSIONS['can-delete-any-comment-anytime']);
        })();

        const onDelete = () => {
            if (!canDelete) {
                return;
            }

            setIsDisabled(true);

            return app('http').route('comments.destroy', {
                id: String(comment.id)
            }, {
                headers: {'content-type': 'application/json'},
                body: {'controller-name': 'Comments'}
            }).then(value => {
                setIsDisabled(false);
                if (onDeleted) {
                    onDeleted(comment);
                }

                return value;
            }).catch(() => {
                setIsDisabled(false);
            });
        };

        const onReply = () => {
            if (onReplyBase != null) {
                onReplyBase(comment);
            }
        };

        return {canDelete, onDelete, onReply};
    }, [comment, isFirstTimeAfterCreation, isOwnComment, onDeleted, onReplyBase, props.allowToDelete, userContext]);

    useEffect(() => {// rerender when `first-time-after-creation` exceeded
        if (!isOwnComment || !isFirstTimeAfterCreation || !createdAt) {
            return;
        }

        const timer = setTimeout(() => {
            setIsDisabled(was => !was);
            setIsDisabled(was => !was);
        }, (createdAt + firstTimeInMinutes * 60) * 1000);

        return () => clearTimeout(timer);
    }, [createdAt, firstTimeInMinutes, isFirstTimeAfterCreation, isOwnComment]);

    return <div className="comment-list__item__controls">
        <RenderIf expression={props.allowToReply !== false && onReplyBase}>
            <Translate
                    tag="button"
                    type="button"
                    message="Chat.controls.reply.button-label"
                    title={trans('Chat.controls.reply.button-tooltip', locale)}
                    className="comment-list__item__reply-button"
                    onClick={onReply}
            />
        </RenderIf>

        <RenderIf expression={canDelete}>
            <ConfirmableButton
                    size="sm"
                    color=""
                    disabled={isDisabled}
                    onClick={onDelete}
                    className="comment-list__item__delete-button"
                    title={trans('Chat.controls.delete.button-tooltip', locale)}
            >
                <i className="fa fa-trash"/>
            </ConfirmableButton>
        </RenderIf>
    </div>;
}
